// UsersTable — the Users list for one role tab. Columns follow the tab: students show courses and
// payment status, tutors show specialisation and counts. Delete is hidden for admins and for yourself;
// the API is the authority on whether a delete is allowed.
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { AdminUserListItem, Role } from "@dm-lms/shared/types";
import PaymentStatusBadge from "../shared/PaymentStatusBadge.js";
import UserIdentityCell from "./UserIdentityCell.js";
import DeleteUserButton from "./DeleteUserButton.js";
import { formatDate } from "../../lib/formatDateTime.js";
import { specialisationLabel } from "../../lib/profileFormOptions.js";

interface Column {
  header: string;
  cell:   (user: AdminUserListItem) => ReactNode;
}

const code: Column     = { header: "Code",       cell: (u) => <span className="font-medium text-dm-navy">{u.code}</span> };
const identity: Column = { header: "Name",       cell: (u) => <UserIdentityCell name={u.name} email={u.email} /> };
const roleLabel: Column = { header: "Role",      cell: (u) => <span className="capitalize text-dm-slate">{u.role}</span> };

const STUDENT_COLUMNS: Column[] = [
  code,
  identity,
  { header: "Courses",        cell: (u) => u.coursesEnrolled ?? 0 },
  {
    header: "Payment status",
    cell:   (u) => (u.worstPaymentStatus ? <PaymentStatusBadge status={u.worstPaymentStatus} /> : <span className="text-dm-slate">—</span>),
  },
  { header: "Member since",   cell: (u) => formatDate(u.createdAt) },
];

const TUTOR_COLUMNS: Column[] = [
  code,
  identity,
  { header: "Specialisation", cell: (u) => specialisationLabel(u.specialisation ?? null) },
  { header: "Courses",        cell: (u) => u.coursesCount ?? 0 },
  { header: "Students",       cell: (u) => u.studentsCount ?? 0 },
];

const COLUMNS_BY_ROLE: Record<Role, Column[]> = {
  student: STUDENT_COLUMNS,
  tutor:   TUTOR_COLUMNS,
  admin:   [code, identity, roleLabel],
};
const ALL_COLUMNS: Column[] = [code, identity, roleLabel];

interface UsersTableProps {
  role:      Role | undefined;
  items:     AdminUserListItem[];
  meId:      string | undefined;
  onDeleted: () => void;
  dimmed:    boolean;
}

export default function UsersTable({ role, items, meId, onDeleted, dimmed }: UsersTableProps) {
  const columns = role ? COLUMNS_BY_ROLE[role] : ALL_COLUMNS;

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${dimmed ? "opacity-60" : ""}`}>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-dm-slate">
          <tr>
            {columns.map((column) => (
              <th key={column.header} className="px-4 py-3 font-medium">{column.header}</th>
            ))}
            <th className="px-4 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((user) => (
            <tr key={user.id}>
              {columns.map((column) => (
                <td key={column.header} className="px-4 py-3">{column.cell(user)}</td>
              ))}
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/app/admin/users/${user.id}`}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-dm-navy hover:bg-gray-50"
                  >
                    View
                  </Link>
                  {user.role !== "admin" && user.id !== meId && (
                    <DeleteUserButton id={user.id} code={user.code} onDeleted={onDeleted} />
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
